declare global {
  interface Window {
    _env_?: { API_BASE_URL?: string };
  }
}

const API_BASE_URL = window._env_?.API_BASE_URL || "http://localhost:8080";

// Errors from apiFetch carry the HTTP status so callers can tell e.g. 404 from 500
export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

const TOKEN_KEY = "token";
const REFRESH_TOKEN_KEY = "refreshToken";

// -----------------------------
// TOKEN STORAGE
// -----------------------------
export function saveTokens(token: string, refreshToken?: string | null) {
  localStorage.setItem(TOKEN_KEY, token);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

// The store registers these so this module doesn't import the store
// (which would create a circular import through the slices).
let onTokenRefreshed: (token: string) => void = () => {};
let onSessionExpired: () => void = () => {};

export function configureAuthHandlers(handlers: {
  onTokenRefreshed: (token: string) => void;
  onSessionExpired: () => void;
}) {
  onTokenRefreshed = handlers.onTokenRefreshed;
  onSessionExpired = handlers.onSessionExpired;
}

// -----------------------------
// REFRESH
// -----------------------------
// Several requests can fail with 401 at once when the access token expires;
// they all wait on the same refresh call, because each refresh token can
// only be used once.
let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return null;

    const data = await res.json();
    saveTokens(data.token, data.refreshToken);
    onTokenRefreshed(data.token);
    return data.token;
  } catch {
    return null;
  }
}

function refreshOnce(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// -----------------------------
// FETCH
// -----------------------------
export async function apiFetch(
  endpoint: string,
  options: RequestInit = {},
  auth: boolean = false
) {
  const send = (token: string | null) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };
    if (auth && token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  };

  let res = await send(localStorage.getItem(TOKEN_KEY));

  // Access token expired: refresh it once and retry, otherwise end the session
  if (res.status === 401 && auth) {
    const newToken = await refreshOnce();
    if (newToken) {
      res = await send(newToken);
    }
    if (res.status === 401) {
      clearTokens();
      onSessionExpired();
      throw new ApiError("Your session has expired. Please log in again.", 401);
    }
  }

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new ApiError(errorData.message || "Something went wrong", res.status);
  }

  const text = await res.text();
  return text ? JSON.parse(text) : null;
}
